import React, { useState, useEffect } from 'react';
import {
    ArrowLeft,
    MessageSquare,
    ThumbsUp,
    ThumbsDown,
    Flag,
    Loader2,
    ChevronLeft,
    ChevronRight,
    Filter,
    X,
    CheckCircle,
    BookOpen,
    UserX,
    Search,

    AlertOctagon,
    Trash2,
    Send,
    Plus,
    Mail,
    Link as LinkIcon,
    Copy
} from 'lucide-react';
import { getAuth } from 'firebase/auth';
import AutoLinkText from './AutoLinkText';

const API_URL = process.env.REACT_APP_API_URL;

const CopyLinkButtonText = () => {
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (copied) {
            const timer = setTimeout(() => setCopied(false), 2000);
            return () => clearTimeout(timer);
        }
    }, [copied]);

    return (
        <span
            onClick={(e) => {
                // Determine if parent click handler needs to be triggered manually or if this propagates.
                // It propagates, so the parent onClick (which does the copying) works.
                // We just need to set state here.
                setCopied(true);
            }}
        >
            {copied ? 'Copied!' : 'Copy Link'}
        </span>
    );
};

const CopyIcon = ({ text }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = (e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <button
            onClick={handleCopy}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            title="Copy User ID"
        >
            {copied ? (
                <CheckCircle className="w-3 h-3 text-green-500" />
            ) : (
                <Copy className="w-3 h-3 text-gray-400" />
            )}
        </button>
    );
};

// Question Detail Modal
const QuestionModal = ({ questionId, onClose }) => {
    const [question, setQuestion] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const auth = getAuth();
    const user = auth.currentUser;

    useEffect(() => {
        const fetchQuestion = async () => {
            if (!user || !questionId) return;

            try {
                const token = await user.getIdToken(true);
                const response = await fetch(`${API_URL}/questions/${questionId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch question');
                }

                const data = await response.json();
                setQuestion(data);
            } catch (err) {
                console.error('Error fetching question:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchQuestion();
    }, [questionId, user]);

    const answerMap = { 'a': 0, 'b': 1, 'c': 2, 'd': 3 };
    const correctIndex = question ? answerMap[question.answer?.toLowerCase()] : -1;
    const options = question ? [
        question.choice_a,
        question.choice_b,
        question.choice_c,
        question.choice_d
    ].filter(Boolean) : [];

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white rounded-t-2xl z-10">
                    <div className="flex items-center gap-3">
                        <h2 className="text-lg font-bold text-gray-800" title={questionId}>
                            Question: {questionId?.slice(0, 8)}...
                        </h2>
                        <button
                            onClick={() => {
                                const link = `${window.location.origin}?question=${questionId}`;
                                navigator.clipboard.writeText(link);
                                // Optional: You could add a toast here, but for now a simple alert or visual cue is good.
                                // Let's use a temporary text change on the button or just a simple alert if we don't have a toast system.
                                // Actually, let's make this button have a "Copied" state.
                            }}
                            className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-xs font-semibold hover:bg-blue-100 transition-all active:scale-95"
                            title="Copy deep link"
                        >
                            <LinkIcon className="w-3 h-3" />
                            <CopyLinkButtonText />
                        </button>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-all"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-slate-500 mb-3" />
                            <p className="text-gray-500">Loading question...</p>
                        </div>
                    ) : error ? (
                        <div className="text-center py-12">
                            <Flag className="w-10 h-10 text-red-400 mx-auto mb-3" />
                            <p className="text-red-600">{error}</p>
                        </div>
                    ) : question ? (
                        <>
                            {/* Question Text */}
                            <p className="text-gray-800 text-lg leading-relaxed mb-6">
                                {question.question}
                            </p>

                            {/* Options */}
                            <div className="space-y-3 mb-6">
                                {options.map((option, index) => {
                                    const isCorrect = index === correctIndex;
                                    const letter = ['A', 'B', 'C', 'D'][index];

                                    return (
                                        <div
                                            key={index}
                                            className={`p-4 rounded-xl border-2 ${isCorrect
                                                ? 'border-green-500 bg-green-50'
                                                : 'border-gray-200 bg-gray-50'
                                                }`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isCorrect
                                                    ? 'bg-green-500 text-white'
                                                    : 'bg-gray-200 text-gray-600'
                                                    }`}>
                                                    {isCorrect ? (
                                                        <CheckCircle className="w-5 h-5" />
                                                    ) : (
                                                        <span className="text-sm font-medium">{letter}</span>
                                                    )}
                                                </div>
                                                <span className={`text-sm ${isCorrect ? 'font-semibold text-green-700' : 'text-gray-700'
                                                    }`}>
                                                    {option}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Explanation */}
                            {question.explanation && (
                                <div className="p-4 rounded-xl bg-blue-50 border-2 border-blue-200 mb-4">
                                    <h3 className="text-sm font-semibold text-blue-800 mb-2">Explanation</h3>
                                    <p className="text-gray-700 text-sm leading-relaxed">
                                        {question.explanation}
                                    </p>
                                </div>
                            )}

                            {/* Heading/Reference */}
                            {question.heading && (
                                <div className="flex items-start gap-2 text-sm text-gray-600">
                                    <BookOpen className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                    <span className="italic">{question.heading}</span>
                                </div>
                            )}
                        </>
                    ) : null}
                </div>

                {/* Footer */}
                <div className="p-4 border-t bg-gray-50 rounded-b-2xl">
                    <button
                        onClick={onClose}
                        className="w-full py-2 px-4 bg-slate-700 text-white rounded-lg font-medium hover:bg-slate-800 transition-all"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

const FeedbackTypeBadge = ({ type }) => {
    const styles = {
        like: 'bg-green-100 text-green-800',
        dislike: 'bg-red-100 text-red-800',
        issue: 'bg-yellow-100 text-yellow-800'
    };

    const icons = {
        like: <ThumbsUp className="w-3 h-3" />,
        dislike: <ThumbsDown className="w-3 h-3" />,
        issue: <Flag className="w-3 h-3" />
    };

    const labels = {
        like: 'Like',
        dislike: 'Dislike',
        issue: 'Issue'
    };

    return (
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[type]}`}>
            {icons[type]}
            {labels[type]}
        </span>
    );
};

const AdminFeedbackPage = ({ onBack }) => {
    const [feedback, setFeedback] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);

    // Active Tab State (feedback | inbox)
    const [activeTab, setActiveTab] = useState('feedback');

    // Inbox State
    const [conversations, setConversations] = useState([]);
    const [inboxLoading, setInboxLoading] = useState(false);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [newMessageText, setNewMessageText] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);

    // New Conversation Modal
    const [newConvModalOpen, setNewConvModalOpen] = useState(false);
    const [newConvUserId, setNewConvUserId] = useState('');
    const [newConvSubject, setNewConvSubject] = useState('');
    const [newConvMessage, setNewConvMessage] = useState('');

    const [filterType, setFilterType] = useState(''); // '', 'like', 'dislike', 'issue'
    const [filterUserId, setFilterUserId] = useState('');
    const [filterQuestionUuid, setFilterQuestionUuid] = useState('');
    const [selectedQuestionUuid, setSelectedQuestionUuid] = useState(null);

    // Lookup State
    const [lookupId, setLookupId] = useState('');
    const [lookupResult, setLookupResult] = useState(null);
    const [lookupLoading, setLookupLoading] = useState(false);
    const [lookupError, setLookupError] = useState(null);

    // Invalid List State
    const [showInvalidList, setShowInvalidList] = useState(false);
    const [invalidQuestions, setInvalidQuestions] = useState([]);
    const [invalidListLoading, setInvalidListLoading] = useState(false);

    // Invalid Reason Modal State
    const [confirmInvalidModalOpen, setConfirmInvalidModalOpen] = useState(false);
    const [targetQuestionIdForInvalid, setTargetQuestionIdForInvalid] = useState(null);
    const [invalidReasonText, setInvalidReasonText] = useState('');

    const auth = getAuth();
    const user = auth.currentUser;

    useEffect(() => {
        if (activeTab === 'feedback') {
            fetchFeedback();
        } else if (activeTab === 'inbox') {
            fetchConversations();
        }
    }, [page, filterType, filterUserId, filterQuestionUuid, activeTab]);

    const fetchConversations = async () => {
        if (!user) return;
        setInboxLoading(true);
        try {
            const token = await user.getIdToken(true);
            const response = await fetch(`${API_URL}/api/conversations`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setConversations(data);
            }
        } catch (err) {
            console.error('Failed to fetch conversations', err);
        } finally {
            setInboxLoading(false);
        }
    };

    const fetchMessages = async (convId) => {
        if (!user) return;
        setMessagesLoading(true);
        try {
            const token = await user.getIdToken(true);
            const response = await fetch(`${API_URL}/api/conversations/${convId}/messages`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setMessages(data);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setMessagesLoading(false);
        }
    };

    const handleSelectConversation = (conv) => {
        setSelectedConversation(conv);
        fetchMessages(conv.id);
    };

    const handleReply = async (e) => {
        e.preventDefault();
        if (!selectedConversation || !newMessageText.trim()) return;

        setSendingMessage(true);
        try {
            const token = await user.getIdToken(true);
            const response = await fetch(`${API_URL}/api/conversations/${selectedConversation.id}/messages`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ content: newMessageText })
            });

            if (response.ok) {
                const savedMsg = await response.json();
                setMessages(prev => [...prev, savedMsg]);
                setNewMessageText('');
                // Update list
                setConversations(prev => prev.map(c =>
                    c.id === selectedConversation.id
                        ? { ...c, last_message: savedMsg.content, updated_at: new Date().toISOString() }
                        : c
                ));
            }
        } catch (err) {
            console.error(err);
            alert('Failed to send reply');
        } finally {
            setSendingMessage(false);
        }
    };

    const handleStartConversation = async () => {
        if (!newConvUserId || !newConvSubject || !newConvMessage) {
            alert('Please fill in all fields');
            return;
        }

        try {
            const token = await user.getIdToken(true);
            const response = await fetch(`${API_URL}/api/conversations`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    user_id: newConvUserId,
                    subject: newConvSubject,
                    initial_message: newConvMessage
                })
            });

            if (!response.ok) {
                const err = await response.json();
                alert(err.detail || 'Failed to create conversation');
                return;
            }

            const newConv = await response.json();
            setConversations(prev => [newConv, ...prev]);
            setNewConvModalOpen(false);
            setNewConvUserId('');
            setNewConvSubject('');
            setNewConvMessage('');

        } catch (err) {
            console.error(err);
            alert('Error creating conversation');
        }
    };

    const fetchFeedback = async () => {
        if (!user) return;

        setLoading(true);
        try {
            const token = await user.getIdToken(true);
            let url = `${API_URL}/api/admin/feedback?page=${page}&page_size=20`;
            if (filterType) {
                url += `&feedback_type=${filterType}`;
            }
            if (filterUserId) {
                url += `&user_id=${encodeURIComponent(filterUserId)}`;
            }
            if (filterQuestionUuid) {
                url += `&question_uuid=${encodeURIComponent(filterQuestionUuid)}`;
            }

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                if (response.status === 403) {
                    throw new Error('Access denied. Admin privileges required.');
                }
                throw new Error('Failed to fetch feedback');
            }

            const data = await response.json();
            setFeedback(data.items);
            setTotalPages(data.total_pages);
            setTotal(data.total);
        } catch (err) {
            console.error('Error fetching feedback:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Helper to display truncated UUID
    const truncateUuid = (uuid) => uuid ? uuid.slice(0, 8) + '...' : '';

    const handleFilterChange = (type) => {
        setFilterType(type);
        setPage(1); // Reset to first page when filter changes
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const truncateText = (text, maxLength = 100) => {
        if (!text || text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    const handleLookup = async (e) => {
        e.preventDefault();
        if (!lookupId) return;

        setLookupLoading(true);
        setLookupError(null);
        setLookupResult(null);

        try {
            const token = await user.getIdToken(true);
            const response = await fetch(`${API_URL}/api/admin/questions/${lookupId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                if (response.status === 404) throw new Error('Question not found');
                throw new Error('Failed to fetch question');
            }

            const data = await response.json();
            setLookupResult(data);
        } catch (err) {
            setLookupError(err.message);
        } finally {
            setLookupLoading(false);
        }
    };

    const handleToggleInvalid = async (questionId, isInvalid) => {
        if (!isInvalid) {
            // If it is NOT invalid, we want to make it invalid -> Open Modal to ask for reason
            setTargetQuestionIdForInvalid(questionId);
            setInvalidReasonText('');
            setConfirmInvalidModalOpen(true);
            return;
        }

        // If it IS invalid, we want to restore it (DELETE) - No reason needed
        await executeInvalidToggle(questionId, 'DELETE');
    };

    const confirmMarkInvalid = async () => {
        if (!targetQuestionIdForInvalid) return;
        const reason = invalidReasonText.trim() || "Marked by admin";
        // Encode reason parameter
        const queryParams = new URLSearchParams({ reason }).toString();
        await executeInvalidToggle(targetQuestionIdForInvalid, 'POST', queryParams);
        setConfirmInvalidModalOpen(false);
    };

    const executeInvalidToggle = async (questionId, method, queryParams = '') => {
        try {
            const token = await user.getIdToken(true);
            let url = `${API_URL}/api/admin/questions/${questionId}/invalid`;

            if (queryParams) {
                url += `?${queryParams}`;
            }

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) throw new Error('Failed to update status');

            // Refresh lookup if active
            if (lookupResult && lookupResult.length > 0 && lookupResult[0].id === questionId) {
                const reFetchResponse = await fetch(`${API_URL}/api/admin/questions/${lookupId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (reFetchResponse.ok) {
                    const data = await reFetchResponse.json();
                    setLookupResult(data);
                }
            }

            // Refresh invalid list if showing
            if (showInvalidList) {
                console.log('Refreshing invalid list...');
                await fetchInvalidQuestions();
            }

            // Refresh feedback list if we are verifying from the list
            if (feedback && feedback.length > 0) {
                // Refresh feedback list to update "Invalid" status badge
                await fetchFeedback();
            }

        } catch (err) {
            console.error(err);
            alert('Error updating question status');
        }
    };

    const handleDeleteFeedback = async (feedbackId) => {
        if (!window.confirm('Are you sure you want to permanently delete this feedback?')) return;

        try {
            const token = await user.getIdToken(true);
            const response = await fetch(`${API_URL}/api/admin/feedback/${feedbackId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to delete feedback');
            }

            // Remove from list
            setFeedback(prev => prev.filter(item => item.id !== feedbackId));
            setTotal(prev => prev - 1);
        } catch (err) {
            console.error('Error deleting feedback:', err);
            alert('Failed to delete feedback');
        }
    };

    const fetchInvalidQuestions = async () => {
        setInvalidListLoading(true);
        try {
            const token = await user.getIdToken(true);
            const response = await fetch(`${API_URL}/api/admin/questions/invalid`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                console.log('Fetched invalid questions:', data.length);
                setInvalidQuestions(data);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setInvalidListLoading(false);
        }
    };

    useEffect(() => {
        if (showInvalidList) {
            fetchInvalidQuestions();
        }
    }, [showInvalidList]);



    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={onBack}
                            className="p-2 bg-white rounded-full shadow-md hover:shadow-lg transition-all"
                        >
                            <ArrowLeft className="w-6 h-6 text-gray-700" />
                        </button>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-800">Admin Dashboard</h1>
                            <p className="text-sm text-gray-500">{activeTab === 'feedback' ? `${total} feedback items` : 'User Messages'}</p>
                        </div>
                    </div>

                    <div className="flex gap-2 bg-white p-1 rounded-xl border border-gray-200 shadow-sm">
                        <button
                            onClick={() => setActiveTab('feedback')}
                            className={`px-4 py-2 rounded-lg font-medium transition-all ${activeTab === 'feedback'
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'text-gray-600 hover:bg-gray-50'
                                }`}
                        >
                            Feedback
                        </button>
                        <button
                            onClick={() => setActiveTab('inbox')}
                            className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${activeTab === 'inbox'
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'text-gray-600 hover:bg-gray-50'
                                }`}
                        >
                            <Mail className="w-4 h-4" /> Inbox
                        </button>
                    </div>
                </div>

                {/* INBOX VIEW */}
                {activeTab === 'inbox' && (
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden min-h-[600px] flex flex-col md:flex-row border border-gray-100 mb-8">

                        {/* Inbox Sidebar */}
                        <div className="w-full md:w-1/3 border-r border-gray-100 flex flex-col">
                            <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                                <h2 className="font-semibold text-gray-700">Conversations</h2>
                                <button
                                    onClick={() => setNewConvModalOpen(true)}
                                    className="p-2 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors"
                                    title="New Message"
                                >
                                    <Plus className="w-4 h-4" />
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto">
                                {conversations.map(conv => (
                                    <button
                                        key={conv.id}
                                        onClick={() => handleSelectConversation(conv)}
                                        className={`w-full text-left p-4 border-b border-gray-50 hover:bg-gray-50 transition-colors ${selectedConversation?.id === conv.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : 'border-l-4 border-l-transparent'}`}
                                    >
                                        <div className="flex justify-between mb-1">
                                            <span className="font-semibold text-gray-800 text-xs">To: {conv.user_id}</span>
                                            <span className="text-xs text-gray-400">{new Date(conv.updated_at).toLocaleDateString()}</span>
                                        </div>
                                        <div className="text-sm text-gray-600 font-medium truncate mb-1">{conv.subject || "No Subject"}</div>
                                        <div className="text-xs text-gray-500 truncate">{conv.last_message}</div>
                                        {conv.unread_count > 0 && <span className="inline-block mt-1 bg-red-100 text-red-600 text-xs px-2 py-0.5 rounded-full">{conv.unread_count} user reply</span>}
                                    </button>
                                ))}
                                {conversations.length === 0 && (
                                    <div className="p-8 text-center text-gray-400">No conversations</div>
                                )}
                            </div>
                        </div>

                        {/* Inbox Message Detail */}
                        <div className="w-full md:w-2/3 flex flex-col bg-gray-50/30">
                            {selectedConversation ? (
                                <>
                                    <div className="p-4 border-b border-gray-200 bg-white">
                                        <h3 className="font-bold text-gray-800">{selectedConversation.subject}</h3>
                                        <span className="text-xs text-gray-500">User: {selectedConversation.user_id}</span>
                                    </div>

                                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                        {messagesLoading ? (
                                            <div className="flex justify-center p-8"><Loader2 className="animate-spin text-gray-400" /></div>
                                        ) : (
                                            messages.map(msg => {
                                                const isAdmin = msg.sender_type === 'admin';
                                                return (
                                                    <div key={msg.id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                                                        <div className={`max-w-[80%] rounded-2xl px-4 py-3 shadow-sm ${isAdmin ? 'bg-blue-600 text-white rounded-tr-none' : 'bg-white text-gray-800 rounded-tl-none border border-gray-200'}`}>
                                                            <p className="whitespace-pre-wrap text-sm">
                                                                <AutoLinkText text={msg.content} linkClassName={isAdmin ? "text-blue-100 underline hover:text-white" : "text-blue-600 underline hover:text-blue-800"} />
                                                            </p>
                                                            <span className={`text-[10px] block mt-1 ${isAdmin ? 'text-blue-200' : 'text-gray-400'}`}>
                                                                {new Date(msg.created_at).toLocaleString()}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )
                                            })
                                        )}
                                    </div>

                                    <div className="p-4 bg-white border-t border-gray-200">
                                        <form onSubmit={handleReply} className="flex gap-2">
                                            <input
                                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                                placeholder="Type a reply..."
                                                value={newMessageText}
                                                onChange={e => setNewMessageText(e.target.value)}
                                            />
                                            <button
                                                disabled={sendingMessage || !newMessageText.trim()}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                            >
                                                <Send className="w-4 h-4" />
                                            </button>
                                        </form>
                                    </div>
                                </>
                            ) : (
                                <div className="flex-1 flex items-center justify-center text-gray-400">
                                    Select a conversation
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* New Conversation Modal */}
                {newConvModalOpen && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-xl font-bold">New Message</h3>
                                <button onClick={() => setNewConvModalOpen(false)}><X className="w-5 h-5 text-gray-400" /></button>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
                                    <input
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        value={newConvUserId}
                                        onChange={e => setNewConvUserId(e.target.value)}
                                        placeholder="Firebase UID"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                                    <input
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        value={newConvSubject}
                                        onChange={e => setNewConvSubject(e.target.value)}
                                        placeholder="Topic"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                                    <textarea
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                                        value={newConvMessage}
                                        onChange={e => setNewConvMessage(e.target.value)}
                                        placeholder="Hello..."
                                    />
                                </div>
                                <div className="flex justify-end gap-2 pt-2">
                                    <button
                                        onClick={() => setNewConvModalOpen(false)}
                                        className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleStartConversation}
                                        disabled={!newConvUserId || !newConvSubject || !newConvMessage}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                    >
                                        Send
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* FEEDBACK View */}
                {activeTab === 'feedback' && (
                    <>
                        <div className="flex flex-col gap-6">
                            <div className="space-y-6">
                                <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                                    <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                                        <Search className="w-5 h-5 text-blue-600" />
                                        Question Lookup
                                    </h2>

                                    <form onSubmit={handleLookup} className="flex gap-4 items-start mb-6">
                                        <div className="flex-1">
                                            <input
                                                type="number"
                                                value={lookupId}
                                                onChange={(e) => setLookupId(e.target.value)}
                                                placeholder="Enter Question ID (e.g. 1)"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                            />
                                        </div>
                                        <button
                                            type="submit"
                                            disabled={!lookupId || lookupLoading}
                                            className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-all shadow-sm"
                                        >
                                            {lookupLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Lookup'}
                                        </button>

                                        <div className="ml-auto">
                                            <button
                                                type="button"
                                                onClick={() => setShowInvalidList(!showInvalidList)}
                                                className={`px-4 py-2 rounded-lg font-medium transition-all shadow-sm flex items-center gap-2 ${showInvalidList
                                                    ? 'bg-red-100 text-red-700 border border-red-200'
                                                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                                    }`}
                                            >
                                                <AlertOctagon className="w-4 h-4" />
                                                {showInvalidList ? 'Hide Invalid List' : 'Show Invalid Questions'}
                                            </button>
                                        </div>
                                    </form>

                                    {/* Invalid Questions List View */}
                                    {showInvalidList && (
                                        <div className="mb-8 border rounded-xl overflow-hidden shadow-sm">
                                            <div className="bg-red-50 px-4 py-3 border-b border-red-100 flex justify-between items-center">
                                                <h3 className="font-bold text-red-800 flex items-center gap-2">
                                                    <AlertOctagon className="w-4 h-4" />
                                                    Blocked Questions ({invalidQuestions.length})
                                                </h3>
                                                <button onClick={fetchInvalidQuestions} className="text-red-600 hover:text-red-800 text-sm">Refresh</button>
                                            </div>
                                            <div className="bg-white max-h-96 overflow-y-auto">
                                                {invalidListLoading ? (
                                                    <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
                                                ) : invalidQuestions.length === 0 ? (
                                                    <div className="p-8 text-center text-gray-500">No invalid questions found.</div>
                                                ) : (
                                                    <table className="w-full text-sm text-left">
                                                        <thead className="text-xs text-gray-700 uppercase bg-gray-50 sticky top-0">
                                                            <tr>
                                                                <th className="px-6 py-3">ID</th>
                                                                <th className="px-6 py-3">Question Text</th>
                                                                <th className="px-6 py-3">Reason</th>
                                                                <th className="px-6 py-3">Action</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {invalidQuestions.map((q) => (
                                                                <tr key={q.question_id} className="border-b hover:bg-gray-50">
                                                                    <td className="px-6 py-4 font-medium">{q.question_id}</td>
                                                                    <td className="px-6 py-4 truncate max-w-xs" title={q.question_text}>{truncateText(q.question_text, 50)}</td>
                                                                    <td className="px-6 py-4">
                                                                        <div className="max-h-24 overflow-y-auto max-w-xs block text-gray-500 text-sm scrollbar-thin scrollbar-thumb-gray-200">
                                                                            {q.reason}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-4">
                                                                        <button
                                                                            onClick={() => handleToggleInvalid(q.question_id, true)}
                                                                            className="text-green-600 hover:text-green-800 font-medium text-xs flex items-center gap-1"
                                                                        >
                                                                            <CheckCircle className="w-3 h-3" /> Restore
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {lookupError && (
                                        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg mb-4">
                                            {lookupError}
                                        </div>
                                    )}

                                    {lookupResult && (
                                        <div className="space-y-6 animate-in fade-in slide-in-from-top-4 duration-300">
                                            {lookupResult.map((q) => (
                                                <div key={q.version} className="border-2 border-slate-200 rounded-xl overflow-hidden">
                                                    <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex justify-between items-center">
                                                        <div className="flex items-center gap-3">
                                                            <span className="font-bold text-slate-700">Version {q.version}</span>
                                                            {q.is_invalid && (
                                                                <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-100 text-red-700 border border-red-200 flex items-center gap-1">
                                                                    <AlertOctagon className="w-3 h-3" /> INVALID
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-4">
                                                            <span className="text-xs text-slate-500 font-mono">UUID: {q.uuid}</span>
                                                            <button
                                                                onClick={() => handleToggleInvalid(q.id, q.is_invalid)}
                                                                className={`text-xs font-medium px-2 py-1 rounded border transition-colors ${q.is_invalid
                                                                    ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                                                                    : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                                                                    }`}
                                                            >
                                                                {q.is_invalid ? 'Restore Question' : 'Mark Invalid'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="p-5">
                                                        <p className="text-lg text-gray-800 mb-4 font-medium">{q.question}</p>

                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                                                            {['choice_a', 'choice_b', 'choice_c', 'choice_d'].map((key, idx) => {
                                                                if (!q[key]) return null;
                                                                const letter = ['A', 'B', 'C', 'D'][idx];
                                                                const isAnswer = q.answer && q.answer.toLowerCase() === letter.toLowerCase();
                                                                return (
                                                                    <div
                                                                        key={key}
                                                                        className={`p-3 rounded-lg border ${isAnswer
                                                                            ? 'bg-green-50 border-green-500 ring-1 ring-green-500'
                                                                            : 'bg-white border-gray-200'
                                                                            }`}
                                                                    >
                                                                        <div className="flex items-start gap-3">
                                                                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${isAnswer ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600'
                                                                                }`}>
                                                                                {letter}
                                                                            </span>
                                                                            <span className={isAnswer ? 'text-green-800 font-medium' : 'text-gray-600'}>
                                                                                {q[key]}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>

                                                        {q.explanation && (
                                                            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-2">
                                                                <h4 className="text-sm font-bold text-blue-800 mb-1">Explanation</h4>
                                                                <p className="text-sm text-blue-900">{q.explanation}</p>
                                                            </div>
                                                        )}

                                                        {q.citations && q.citations.length > 0 && (
                                                            <div className="mt-4 pt-4 border-t border-gray-100">
                                                                <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Source</h4>
                                                                {q.citations.map((c, i) => (
                                                                    <p key={i} className="text-sm text-gray-600 italic">
                                                                        {c.text}
                                                                    </p>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="bg-white rounded-xl shadow-md p-4 mb-6 space-y-4">
                                    {/* Type Filter */}
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Filter className="w-5 h-5 text-gray-500" />
                                        <span className="text-sm text-gray-600 mr-2">Type:</span>
                                        <button
                                            onClick={() => handleFilterChange('')}
                                            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${filterType === ''
                                                ? 'bg-slate-800 text-white'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                }`}
                                        >
                                            All
                                        </button>
                                        <button
                                            onClick={() => handleFilterChange('like')}
                                            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1 ${filterType === 'like'
                                                ? 'bg-green-600 text-white'
                                                : 'bg-green-50 text-green-700 hover:bg-green-100'
                                                }`}
                                        >
                                            <ThumbsUp className="w-4 h-4" /> Likes
                                        </button>
                                        <button
                                            onClick={() => handleFilterChange('dislike')}
                                            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1 ${filterType === 'dislike'
                                                ? 'bg-red-600 text-white'
                                                : 'bg-red-50 text-red-700 hover:bg-red-100'
                                                }`}
                                        >
                                            <ThumbsDown className="w-4 h-4" /> Dislikes
                                        </button>
                                        <button
                                            onClick={() => handleFilterChange('issue')}
                                            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1 ${filterType === 'issue'
                                                ? 'bg-yellow-600 text-white'
                                                : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'
                                                }`}
                                        >
                                            <Flag className="w-4 h-4" /> Issues
                                        </button>
                                    </div>

                                    {/* User and Question Filters */}
                                    <div className="flex items-center gap-4 flex-wrap">
                                        <div className="flex items-center gap-2">
                                            <label className="text-sm text-gray-600">User:</label>
                                            <input
                                                type="text"
                                                value={filterUserId}
                                                onChange={(e) => {
                                                    setFilterUserId(e.target.value);
                                                    setPage(1);
                                                }}
                                                placeholder="User ID"
                                                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm w-32 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                            {filterUserId && (
                                                <button
                                                    onClick={() => { setFilterUserId(''); setPage(1); }}
                                                    className="text-gray-400 hover:text-gray-600"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <label className="text-sm text-gray-600">Question:</label>
                                            <input
                                                type="text"
                                                value={filterQuestionUuid}
                                                onChange={(e) => {
                                                    setFilterQuestionUuid(e.target.value);
                                                    setPage(1);
                                                }}
                                                placeholder="UUID"
                                                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm w-32 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                            {filterQuestionUuid && (
                                                <button
                                                    onClick={() => { setFilterQuestionUuid(''); setPage(1); }}
                                                    className="text-gray-400 hover:text-gray-600"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>

                                        {(filterUserId || filterQuestionUuid) && (
                                            <button
                                                onClick={() => {
                                                    setFilterUserId('');
                                                    setFilterQuestionUuid('');
                                                    setFilterType('');
                                                    setPage(1);
                                                }}
                                                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                            >
                                                Clear all filters
                                            </button>
                                        )}
                                    </div>
                                </div>

                            </div>
                            <div className="w-full">
                                {loading ? (
                                    <div className="flex flex-col items-center justify-center py-20">
                                        <Loader2 className="w-12 h-12 animate-spin text-slate-500 mb-4" />
                                        <p className="text-gray-600">Loading feedback...</p>
                                    </div>
                                ) : error ? (
                                    <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 text-center">
                                        <Flag className="w-12 h-12 text-red-500 mx-auto mb-3" />
                                        <p className="text-red-700 font-semibold">Error</p>
                                        <p className="text-sm text-red-600 mt-1">{error}</p>
                                    </div>
                                ) : feedback.length === 0 ? (
                                    <div className="bg-white rounded-2xl p-8 text-center">
                                        <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                        <p className="text-gray-600 mb-2">No feedback found</p>
                                        <p className="text-sm text-gray-500">
                                            {filterType ? 'Try changing the filter' : 'Users haven\'t submitted any feedback yet'}
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        {/* Feedback List */}
                                        <div className="space-y-4">
                                            {feedback.map((item) => (
                                                <div key={item.id} className="bg-white rounded-xl shadow-md p-5">
                                                    <div className="flex items-start justify-between gap-4 mb-3">
                                                        <div className="flex items-center gap-3">
                                                            <button
                                                                onClick={() => setSelectedQuestionUuid(item.question_uuid)}
                                                                className="text-sm font-mono bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200 transition-all"
                                                                title={item.question_uuid}
                                                            >
                                                                Q:{truncateUuid(item.question_uuid)}
                                                            </button>
                                                            <span className="text-xs font-medium px-2 py-0.5 rounded bg-purple-100 text-purple-700">
                                                                v{item.question_version}
                                                            </span>
                                                            <FeedbackTypeBadge type={item.feedback_type} />

                                                            {item.is_invalid && (
                                                                <span className="flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-600 border border-red-200">
                                                                    <AlertOctagon className="w-3 h-3" /> INVALID
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            <button
                                                                onClick={() => handleToggleInvalid(item.question_id, item.is_invalid)}
                                                                className={`text-xs font-medium px-2 py-1 rounded border transition-colors ${item.is_invalid
                                                                    ? 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100'
                                                                    : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                                                                    }`}
                                                            >
                                                                {item.is_invalid ? 'Restore' : 'Invalid'}
                                                            </button>
                                                            <span className="text-xs text-gray-500">
                                                                {formatDate(item.created_at)}
                                                            </span>
                                                            <button
                                                                onClick={() => handleDeleteFeedback(item.id)}
                                                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                                                title="Delete Feedback"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <button
                                                        onClick={() => setSelectedQuestionUuid(item.question_uuid)}
                                                        className="text-left text-gray-800 mb-3 text-sm leading-relaxed hover:text-blue-700 transition-all cursor-pointer"
                                                    >
                                                        {truncateText(item.question_text, 200)}
                                                    </button>

                                                    {
                                                        item.comment && (
                                                            <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-slate-400">
                                                                <p className="text-xs text-gray-500 mb-1">Comment:</p>
                                                                <p className="text-gray-700 text-sm">{item.comment}</p>
                                                            </div>
                                                        )
                                                    }

                                                    < div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between" >
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                onClick={() => {
                                                                    setFilterUserId(item.user_id);
                                                                    setPage(1);
                                                                }}
                                                                className="text-xs text-gray-400 hover:text-blue-600 transition-all"
                                                                title="Filter by this user"
                                                            >
                                                                User: {item.user_id.substring(0, 8)}...
                                                            </button>
                                                            <CopyIcon text={item.user_id} />
                                                            {item.user_is_deleted && (
                                                                <span
                                                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700"
                                                                    title={item.user_deleted_at ? `Deleted on ${formatDate(item.user_deleted_at)}` : 'Deleted'}
                                                                >
                                                                    <UserX className="w-3 h-3" />
                                                                    Deleted
                                                                </span>
                                                            )}
                                                        </div>
                                                        <button
                                                            onClick={() => {
                                                                setFilterQuestionUuid(item.question_uuid);
                                                                setPage(1);
                                                            }}
                                                            className="text-xs text-gray-400 hover:text-blue-600 transition-all"
                                                            title="See all feedback for this question"
                                                        >
                                                            All feedback for Q:{truncateUuid(item.question_uuid)}
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {/* Pagination */}
                                        {totalPages > 1 && (
                                            <div className="flex items-center justify-center gap-4 mt-6">
                                                <button
                                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                                    disabled={page === 1}
                                                    className={`flex items-center gap-1 px-4 py-2 rounded-lg font-medium transition-all ${page === 1
                                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                        : 'bg-white text-gray-700 hover:bg-gray-50 shadow-md'
                                                        }`}
                                                >
                                                    <ChevronLeft className="w-4 h-4" /> Previous
                                                </button>

                                                <span className="text-sm text-gray-600">
                                                    Page {page} of {totalPages}
                                                </span>

                                                <button
                                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                                    disabled={page === totalPages}
                                                    className={`flex items-center gap-1 px-4 py-2 rounded-lg font-medium transition-all ${page === totalPages
                                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                        : 'bg-white text-gray-700 hover:bg-gray-50 shadow-md'
                                                        }`}
                                                >
                                                    Next <ChevronRight className="w-4 h-4" />
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Question Modal */}
                            {selectedQuestionUuid && (
                                <QuestionModal
                                    questionId={selectedQuestionUuid}
                                    onClose={() => setSelectedQuestionUuid(null)}
                                />
                            )}

                            {/* Confirm Invalid Modal */}
                            {confirmInvalidModalOpen && (
                                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                                    <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6 animate-in zoom-in-95 duration-200">
                                        <div className="flex justify-between items-start mb-4">
                                            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                                <AlertOctagon className="w-6 h-6 text-red-600" /> Mark as Invalid
                                            </h3>
                                            <button
                                                onClick={() => setConfirmInvalidModalOpen(false)}
                                                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                                            >
                                                <X className="w-6 h-6 text-gray-400" />
                                            </button>
                                        </div>

                                        <div className="mb-6">
                                            <p className="text-gray-600 mb-4">
                                                Are you sure you want to mark this question as invalid? Users will generally not see this question unless they are auditing.
                                            </p>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Reason (Optional)
                                            </label>
                                            <textarea
                                                value={invalidReasonText}
                                                onChange={(e) => setInvalidReasonText(e.target.value)}
                                                placeholder="e.g., duplicate content, incorrect data, replaced by v2..."
                                                className="w-full h-24 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:outline-none resize-none text-base"
                                            />
                                        </div>

                                        <div className="flex justify-end gap-3">
                                            <button
                                                onClick={() => setConfirmInvalidModalOpen(false)}
                                                className="px-5 py-2.5 text-gray-600 font-medium hover:bg-gray-100 rounded-xl transition-colors"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={confirmMarkInvalid}
                                                className="px-5 py-2.5 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-all shadow-md"
                                            >
                                                Mark Invalid
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )
                }
            </div >
        </div >
    );
};

export default AdminFeedbackPage;
